/**
 * Custom Drawer Header/Footer Updater
 * Hooks into theme cart events to update header and footer
 */

(function() {
  'use strict';

  const cartPage = document.querySelector('[data-cart-page]');
  const isCartPage = Boolean(cartPage);

  function slugifyReadable(value) {
    return String(value || '')
      .replace(/[åÅ]/g, 'a')
      .replace(/[äÄ]/g, 'a')
      .replace(/[öÖ]/g, 'o')
      .replace(/[ëË]/g, 'e')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'na';
  }

  function getDrawerArea(el) {
    if (el.closest('.drawer-upsell-section, .upsell-card, .upsell-cta')) return 'upsell';
    if (el.closest('.cart-drawer__head')) return 'cart-drawer-head';
    if (el.closest('.cart-drawer__body')) return 'cart-drawer-body';
    if (el.closest('.footer-regular')) return 'footer-regular';
    if (el.closest('.checkout-bar-fixed')) return 'checkout-bar-fixed';
    return 'drawer';
  }

  function getDrawerProductName(el) {
    const explicit = el.getAttribute('data-product-title') || el.getAttribute('data-product-key');
    if (explicit) return explicit;

    const item = el.closest('[data-cart-item], .product-item, .upsell-card');
    if (item) {
      const itemTitle = item.getAttribute('data-product-title') || item.getAttribute('data-product-key') || item.getAttribute('data-item-title');
      if (itemTitle) return itemTitle;

      const titleEl = item.querySelector('.drawer-card-title, .upsell-title, [data-product-title]');
      if (titleEl && titleEl.textContent) return titleEl.textContent;
    }

    return '';
  }

  function getDrawerElementType(el) {
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'a') return 'btn';
    if (tag === 'img') return 'img';
    if (tag === 'div') return 'div';
    if (tag === 'input') {
      const type = (el.getAttribute('type') || '').toLowerCase();
      return type === 'submit' ? 'btn' : (type || 'input');
    }
    return tag === 'button' ? 'btn' : 'element';
  }

  function getDrawerLabel(el) {
    const className = el.className || '';
    const quantityLike =
      el.hasAttribute('data-quantity-minus') ||
      el.hasAttribute('data-quantity-plus') ||
      el.hasAttribute('data-quantity-field') ||
      /\bquantity-btn\b/.test(className) ||
      /\bquantity-input\b/.test(className);

    if (quantityLike) {
      let action = 'quantity';
      if (el.hasAttribute('data-quantity-plus') || /\bincrease\b/.test(className) || /\bplus\b/.test(className)) action = 'add';
      if (el.hasAttribute('data-quantity-minus') || /\bdecrease\b/.test(className) || /\bminus\b/.test(className)) {
        action = el.classList.contains('is-remove') ? 'remove' : 'decrease';
      }
      const productName = slugifyReadable(getDrawerProductName(el));
      return productName !== 'na' ? `${action}-${productName}` : action;
    }

    const rawLabel = el.getAttribute('aria-label') || el.getAttribute('title') || el.textContent || '';
    const label = slugifyReadable(rawLabel);
    return label !== 'na' ? label : 'item';
  }

  function applyDrawerReadableIds() {
    const drawerRoot = document.querySelector('.cart-drawer');
    if (!drawerRoot) return;

    const counts = {};
    drawerRoot.querySelectorAll('button, a[href], input[type="submit"], input[type="button"]').forEach((el) => {
      const area = getDrawerArea(el);
      const type = slugifyReadable(getDrawerElementType(el));
      const label = slugifyReadable(getDrawerLabel(el));
      const baseId = `drawer-${area}-${type}-${label}`;
      counts[baseId] = (counts[baseId] || 0) + 1;
      const suffix = counts[baseId] > 1 ? `-${counts[baseId]}` : '';
      el.id = `${baseId}${suffix}`;
    });
  }

  function extractCartData(headerInfo, footerInfo) {
    return {
      itemCount: parseInt(headerInfo.querySelector('[data-cart-items-count]')?.textContent || '0', 10),
      originalSubtotalRaw: parseInt(footerInfo.querySelector('[data-original-subtotal]')?.textContent || '0', 10),
      finalTotalRaw: parseInt(footerInfo.querySelector('[data-final-total]')?.textContent || '0', 10),
      finalTotalFormatted: footerInfo.querySelector('[data-final-total-formatted]')?.textContent || '',
      originalSubtotalFormatted: footerInfo.querySelector('[data-original-subtotal-formatted]')?.textContent || '',
      totalDiscountsRaw: parseInt(headerInfo.querySelector('[data-total-discounts]')?.textContent || '0', 10),
      totalDiscountsFormatted: headerInfo.querySelector('[data-total-discounts-formatted]')?.textContent || '',
      discountCodeRaw: parseInt(headerInfo.querySelector('[data-discount-code-total]')?.textContent || '0', 10),
      discountCodeFormatted: headerInfo.querySelector('[data-discount-code-formatted]')?.textContent || '',
      lineDiscountRaw: parseInt(headerInfo.querySelector('[data-line-discount-total]')?.textContent || '0', 10),
      lineDiscountFormatted: headerInfo.querySelector('[data-line-discount-formatted]')?.textContent || '',
      subscriptionDiscountRaw: parseInt(headerInfo.querySelector('[data-subscription-discount]')?.textContent || '0', 10),
      subscriptionDiscountFormatted: headerInfo.querySelector('[data-subscription-discount-formatted]')?.textContent || '',
      showOriginalTotal: footerInfo.querySelector('[data-show-original-total]')?.textContent?.trim() === 'true',
    };
  }

  async function fetchCartData() {
    try {
      const response = await fetch('/cart?section_id=custom-api-cart-items');
      if (!response.ok) return null;
      const text = await response.text();
      const element = document.createElement('div');
      element.innerHTML = text;
      const headerInfo = element.querySelector('[data-api-header-info]');
      const footerInfo = element.querySelector('[data-api-footer-info]');
      if (!headerInfo || !footerInfo) return null;
      return extractCartData(headerInfo, footerInfo);
    } catch (error) {
      return null;
    }
  }

  function applyUpdates(data) {
    if (!data) return null;

    const itemCount = data.itemCount;
    const finalTotalFormatted = data.finalTotalFormatted;
    const originalSubtotalFormatted = data.originalSubtotalFormatted;
    const originalSubtotalRaw = Number.isFinite(data.originalSubtotalRaw) ? data.originalSubtotalRaw : null;
    const totalDiscountsRaw = data.totalDiscountsRaw;
    const totalDiscountsFormatted = data.totalDiscountsFormatted;
    const discountCodeRaw = data.discountCodeRaw;
    const discountCodeFormatted = data.discountCodeFormatted;
    const lineDiscountRaw = data.lineDiscountRaw;
    const lineDiscountFormatted = data.lineDiscountFormatted;
    const subscriptionDiscountRaw = data.subscriptionDiscountRaw;
    const subscriptionDiscountFormatted = data.subscriptionDiscountFormatted;

    const headerCartQty = document.querySelector('.cart-drawer__head [data-cart-items-qty]');
    if (headerCartQty) {
      const itemsText = itemCount === 1 ? 'artikel' : 'artiklar';
      headerCartQty.textContent = ` (${itemCount} ${itemsText})`;
      headerCartQty.classList.toggle('hidden', itemCount === 0);
    }

    const shouldHideDiscounts = !totalDiscountsRaw || totalDiscountsRaw <= 0 || itemCount === 0;
    const updateDiscountGroup = (detailsEl, totalSelector, lineAttr, valueAttr) => {
      if (!detailsEl) return;
      detailsEl.classList.toggle('hidden', shouldHideDiscounts);

      const totalEl = detailsEl.querySelector(totalSelector);
      if (totalEl) {
        totalEl.textContent = shouldHideDiscounts ? '' : totalDiscountsFormatted;
      }

      const updateLine = (key, rawValue, formattedValue) => {
        const line = detailsEl.querySelector(`[${lineAttr}="${key}"]`);
        if (!line) return;
        line.classList.toggle('hidden', shouldHideDiscounts || !rawValue || rawValue <= 0);
        const valueEl = line.querySelector(`[${valueAttr}="${key}"]`);
        if (valueEl) {
          valueEl.textContent = formattedValue || '';
        }
      };

      updateLine('discount-code', discountCodeRaw, discountCodeFormatted);
      updateLine('line-discount', lineDiscountRaw, lineDiscountFormatted);
      updateLine('subscription-discount', subscriptionDiscountRaw, subscriptionDiscountFormatted);
    };

    const footerPrices = document.querySelectorAll('.cart-drawer [data-cart-total-price]');
    footerPrices.forEach(el => el.textContent = finalTotalFormatted);
    const subtotalEl = document.querySelector('.footer-summary [data-cart-total]');
    if (subtotalEl && originalSubtotalFormatted) {
      subtotalEl.textContent = originalSubtotalFormatted;
    }
    if (subtotalEl && originalSubtotalRaw !== null) {
      subtotalEl.setAttribute('data-cart-total', originalSubtotalRaw);
    }

    const originalTotalEl = document.querySelector('.checkout-bar-fixed [data-cart-original-total]');
    if (originalTotalEl) {
      const showOriginal = data.showOriginalTotal;
      originalTotalEl.classList.toggle('hidden', !showOriginal);
      if (originalSubtotalFormatted) {
        originalTotalEl.textContent = originalSubtotalFormatted;
      }
    }

    const footerCountEl = document.querySelector('.footer-summary .dot-separator-small + span');
    if (footerCountEl) {
      const itemsText = itemCount === 1 ? 'artikel' : 'artiklar';
      footerCountEl.textContent = `${itemCount} ${itemsText}`;
    }

    // Update both header and footer discount details using the same shared function
    const headerDiscountDetails = document.querySelector('[data-header-discount-details]');
    updateDiscountGroup(
      headerDiscountDetails,
      '[data-header-discount-total]',
      'data-header-discount-line',
      'data-header-discount-value'
    );

    const footerDiscountDetails = document.querySelector('[data-footer-discount-details]');
    updateDiscountGroup(
      footerDiscountDetails,
      '[data-discount-total]',
      'data-discount-line',
      'data-discount-value'
    );

    const quantityInputs = document.querySelectorAll('.cart-drawer [data-quantity-field]');
    quantityInputs.forEach((input) => {
      const qtyValue = parseInt(input.value || '0', 10);
      const holder = input.closest('.quantity-selector');
      const decreaseBtn = holder ? holder.querySelector('[data-quantity-minus]') : null;
      if (decreaseBtn) {
        decreaseBtn.classList.toggle('is-remove', qtyValue <= 1);
      }
    });

    return itemCount;
  }

  async function updateElements() {
    const dataFromWindow = isCartPage ? null : (window.customDrawerData || null);
    if (dataFromWindow) {
      return applyUpdates(dataFromWindow);
    }

    const headerInfo = document.querySelector('[data-api-header-info]');
    const footerInfo = document.querySelector('[data-api-footer-info]');
    if (headerInfo && footerInfo) {
      const extractedData = extractCartData(headerInfo, footerInfo);
      window.customDrawerData = extractedData;
      return applyUpdates(extractedData);
    }

    if (!isCartPage) return null;

    const fetchedData = await fetchCartData();
    if (fetchedData) {
      window.customDrawerData = fetchedData;
      return applyUpdates(fetchedData);
    }

    return null;
  }

  function getCartTotals() {
    const cartItems = document.querySelectorAll('.cart-drawer [data-cart-item]');
    let foodShakeQty = 0;
    let completeProteinQty = 0;

    cartItems.forEach((item) => {
      const quantityInput = item.querySelector('[data-quantity-field]');
      if (quantityInput) {
        const quantity = Number(quantityInput.value) || 0;

        if (item.hasAttribute('data-product-type')) {
          const productType = item.getAttribute('data-product-type');
          if (productType === 'food-shake') {
            foodShakeQty += quantity;
          } else if (productType === 'complete-protein') {
            completeProteinQty += quantity;
          }
        }
      }
    });

    return {
      foodShakeQty,
      completeProteinQty,
      itemCount: cartItems.length,
    };
  }

  function updateMinimumWarningCard(isValid, errorMessage, itemCount) {
    const warningCards = document.querySelectorAll('[data-minimum-warning-card]');
    warningCards.forEach((card) => {
      const messageEl = card.querySelector('[data-minimum-warning-message]');
      const shouldShow = !isValid && itemCount > 0 && Boolean(errorMessage);
      card.classList.toggle('hidden', !shouldShow);
      card.style.display = shouldShow ? '' : 'none';
      if (messageEl) {
        messageEl.textContent = shouldShow ? errorMessage : '';
      }
    });
  }

  function updateCheckoutButtons(isValid, errorMessage) {
    const checkoutButtons = document.querySelectorAll('.cart-drawer .btn-checkout, .cart-drawer .checkout-btn-fixed');
    checkoutButtons.forEach((checkoutButton) => {
      if (!checkoutButton) return;
      if (!isValid) {
        checkoutButton.disabled = true;
        checkoutButton.classList.add('is-disabled');
        checkoutButton.style.opacity = '0.6';
        checkoutButton.style.pointerEvents = 'none';
        checkoutButton.setAttribute('aria-disabled', 'true');
        checkoutButton.title = errorMessage || 'Cart requirements not met';
      } else {
        checkoutButton.disabled = false;
        checkoutButton.classList.remove('is-disabled');
        checkoutButton.style.opacity = '1';
        checkoutButton.style.pointerEvents = 'auto';
        checkoutButton.removeAttribute('aria-disabled');
        checkoutButton.title = '';
      }
    });
  }

  async function validateMinimumsAndUpdate() {
    if (!document.querySelector('.cart-drawer')) {
      return { isValid: true, errorMessage: '', itemCount: 0 };
    }

    const totals = getCartTotals();
    const isValid = totals.itemCount > 0;
    const errorMessage = isValid ? '' : 'Lägg till produkter i varukorgen.';

    updateMinimumWarningCard(isValid, errorMessage, totals.itemCount);
    updateCheckoutButtons(isValid, errorMessage);
    return { isValid, errorMessage, itemCount: totals.itemCount };
  }

  function attachCheckoutGuard() {
    const drawerForm = document.querySelector('#cartDrawerCheckoutForm');
    if (!drawerForm || drawerForm.dataset.minimumGuarded === 'true') return;
    drawerForm.dataset.minimumGuarded = 'true';

    drawerForm.addEventListener('submit', async (event) => {
      const validation = await validateMinimumsAndUpdate();
      if (!validation.isValid) {
        event.preventDefault();
      }
    });
  }

  window.customCartMinimums = window.customCartMinimums || {};
  window.customCartMinimums.validateAndUpdate = validateMinimumsAndUpdate;

  function unlockCartControls() {
    if (!isCartPage) return;
    const scope = cartPage || document;
    const controls = scope.querySelectorAll('button, input');
    controls.forEach((el) => {
      el.classList.remove('is-disabled');
      el.removeAttribute('disabled');
      el.disabled = false;
    });
    const loadingContainers = scope.querySelectorAll('.cart-drawer.is-loading');
    loadingContainers.forEach((container) => container.classList.remove('is-loading'));
    const loadingItems = scope.querySelectorAll('.cart__item.is-loading, .cart__item.is-disabled');
    loadingItems.forEach((item) => item.classList.remove('is-loading', 'is-disabled'));
  }
  
  // Helper function to get item count from cart directly if API containers aren't available
  async function getItemCountFromCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.item_count || 0;
    } catch (error) {
      return 0;
    }
  }

  // Enhanced update function that also handles empty state
  async function updateElementsWithEmptyState() {
    // First try the normal update
    const updatedItemCount = await updateElements();
    
    // Always check cart state to ensure empty message is shown/hidden correctly
    // This handles cases where the drawer rebuilds and the API containers aren't available yet
    const headerInfo = document.querySelector('[data-api-header-info]');
    const footerInfo = document.querySelector('[data-api-footer-info]');
    const dataFromWindow = window.customDrawerData || null;
    
    // Get item count from multiple sources
    let itemCount = 0;
    if (updatedItemCount !== null && updatedItemCount !== undefined) {
      itemCount = updatedItemCount;
    } else if (dataFromWindow && dataFromWindow.itemCount !== undefined) {
      itemCount = dataFromWindow.itemCount;
    } else if (headerInfo) {
      const countEl = headerInfo.querySelector('[data-cart-items-count]');
      if (countEl) {
        itemCount = parseInt(countEl.textContent || '0', 10);
      }
    } else {
      const cartItems = document.querySelectorAll('.cart-drawer [data-cart-item]');
      itemCount = cartItems.length;

      if (itemCount === 0) {
        itemCount = await getItemCountFromCart();
      }
    }
    
    const isEmpty = itemCount === 0;
    
    // Update empty state elements
    const itemsHolder = document.querySelector('#cartItemsHolder');
    const emptyMessage = document.querySelector('#cartDrawerEmptyMessage');
    const infoCards = document.querySelectorAll('.cart-drawer__head .drawer-card');
    const headerActions = document.querySelector('.header-actions');
    const extraPromoCard = document.querySelector('.drawer-card--extra');
    const footerRegular = document.querySelector('.footer-regular');

    if (itemsHolder) {
      itemsHolder.classList.toggle('hidden', isEmpty);
    }

    if (emptyMessage) {
      emptyMessage.classList.toggle('hidden', !isEmpty);
    }

    infoCards.forEach(card => {
      if (card.hasAttribute('data-minimum-warning-card')) return;
      card.classList.toggle('hidden', isEmpty);
      card.style.display = isEmpty ? 'none' : '';
    });

    if (headerActions) {
      headerActions.classList.toggle('hidden', isEmpty);
      headerActions.style.display = isEmpty ? 'none' : '';
    }

    // Ensure empty cart action buttons are always clickable
    const emptyActions = document.querySelectorAll('.drawer-empty-actions a, .drawer-empty-actions .checkout-btn-fixed');
    emptyActions.forEach(button => {
      button.style.opacity = '1';
      button.style.pointerEvents = 'auto';
      button.style.cursor = 'pointer';
    });

    if (extraPromoCard) {
      extraPromoCard.classList.toggle('hidden', isEmpty);
      extraPromoCard.style.display = isEmpty ? 'none' : '';
    }

    // Note: Subscription banner is now handled by Liquid in the AJAX response
    // The banner will be automatically shown/hidden based on cart state when the drawer updates
    // No JavaScript manipulation needed - Liquid condition handles it

    if (footerRegular) {
      footerRegular.classList.toggle('hidden', isEmpty);
      footerRegular.style.display = isEmpty ? 'none' : '';
    }

    applyDrawerReadableIds();
    unlockCartControls();
    await validateMinimumsAndUpdate();
    attachCheckoutGuard();
  }
  
  // Listen to cart events
  document.addEventListener('theme:cart:added', async () => {
    window.customDrawerData = null;
    const freshData = await fetchCartData();
    if (freshData) {
      window.customDrawerData = freshData;
    }
    await updateElementsWithEmptyState();
  });
  
  document.addEventListener('theme:cart:update', () => {
    window.customDrawerData = null;
    // Delay to ensure drawer has finished rebuilding, then fetch fresh data
    setTimeout(async () => {
      const freshData = await fetchCartData();
      if (freshData) {
        window.customDrawerData = freshData;
      }
      await updateElementsWithEmptyState();
    }, 300);
  });
  
  // Also listen for when cart drawer content is rebuilt
  // This handles cases where the drawer rebuilds its template
  let updateTimeout = null;
  const observer = new MutationObserver((mutations) => {
    const drawer = document.querySelector('#cart-drawer');
    const itemsHolder = document.querySelector('#cartItemsHolder');
    const emptyMessage = document.querySelector('#cartDrawerEmptyMessage');
    
    // Only trigger update if relevant elements changed
    const hasRelevantChange = mutations.some(mutation => {
      return Array.from(mutation.addedNodes).some(node => {
        if (node.nodeType === 1) { // Element node
          return node === itemsHolder || node === emptyMessage || 
                 node.contains && (node.contains(itemsHolder) || node.contains(emptyMessage));
        }
        return false;
      });
    });
    
    if (hasRelevantChange) {
      // Debounce updates
      if (updateTimeout) clearTimeout(updateTimeout);
      updateTimeout = setTimeout(() => {
        updateElementsWithEmptyState();
      }, 200);
    }
  });
  
  // Observe the cart drawer body for changes
  const cartDrawerBody = document.querySelector('.cart-drawer__body');
  if (cartDrawerBody) {
    observer.observe(cartDrawerBody, {
      childList: true,
      subtree: false
    });
  }
  
  // Also check on initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        updateElementsWithEmptyState();
      }, 500);
    });
  } else {
    setTimeout(() => {
      updateElementsWithEmptyState();
    }, 500);
  }
  
})();
